import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "../../../styles/pages/ouber.scss";
import { Space, Button, Typography, Input, Select, Row, Col, Modal, Tooltip, Tour } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  PlusCircleOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  DeleteOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import PvTable from "../../../components/Table/PvTable";
import DataView from "../../../components/DataView/DataView";
import PvCardList from "../../../components/DataView/PvCardList";
import AddDriverModal from "../../../components/Ouber/modals/admin/AdminAddDriverModal";
import AdminViewDriverModal from "../../../components/Ouber/modals/admin/AdminViewDriverModal";
import OuberPageInfoIcon from "../../../components/Ouber/OuberPageInfoIcon";
import { useOuberService } from "../../../services";
import { showErrorToast, showSuccessToast } from "../../../components/Ouber/ToastNotification";

/**
 * The purpose of this page is to manage the roster of drivers, 
 * track their contact details, and view their assigned trips.
 */
const ManageDriverPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const { dataView } = useSelector((state) => state.generalSlice);
  const { details: profile, account_types = [] } = useSelector((state) => state.accountDetailSlice);
  const isSuperAdmin = profile?.account_type === "Super Admin" || account_types.includes("superadmin") || account_types.includes("xadmin");
  const canManage = (record) => isSuperAdmin || (record.created_by_employee_id && record.created_by_employee_id === profile?.id);

  const [filterDriver, setFilterDriver] = useState("");
  const [filterContact, setFilterContact] = useState("");
  const [filterType, setFilterType] = useState(null);
  const [filterAssignment, setFilterAssignment] = useState(null);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const createDriverButtonRef = useRef(null);
  const filtersRef = useRef(null);
  const viewControlsRef = useRef(null);
  const tableRef = useRef(null);

  const { service: ouberService, loading } = useOuberService();
  const [data, setData] = useState([]);

  const tourSeenStorageKey = profile?.email
    ? `ouber-drivers-tour-seen:${profile.email}`
    : null;

  useEffect(() => {
    if (!tourSeenStorageKey) return;

    try {
      if (!window.localStorage.getItem(tourSeenStorageKey)) {
        setIsTourOpen(true);
      }
    } catch {
      // localStorage can be unavailable (private browsing, etc.) — the tour
      // simply won't auto-open in that case, no need to fail loudly.
    }
  }, [tourSeenStorageKey]);

  const handleTourClose = () => {
    setIsTourOpen(false);

    if (!tourSeenStorageKey) return;

    try {
      window.localStorage.setItem(tourSeenStorageKey, "1");
    } catch {
      // Ignore — worst case the tour auto-opens again next visit.
    }
  };

  const tourSteps = useMemo(
    () => [
      {
        title: "Add a driver",
        description: "Register a new driver, along with their contact info and type.",
        target: () => createDriverButtonRef.current,
      },
      {
        title: "Search and filter",
        description:
          "Narrow the list down by name, contact info, driver type, or whether they already have an assignment.",
        target: () => filtersRef.current,
      },
      {
        title: "Table or card view",
        description: "Switch layouts, or refresh the list to pull the latest data.",
        target: () => viewControlsRef.current,
      },
      {
        title: "View, edit, or remove a driver",
        description:
          "Use the icons on each row to view details, edit info, or delete a driver (deleting requires Super Admin).",
        target: () => tableRef.current,
      },
    ],
    [],
  );

  // Retrieves the driver list from the backend with filters for name, contact, type, and assignment status
  const fetchDrivers = useCallback(async () => {
    try {
      const params = {
        search_name: filterDriver || undefined,
        contact_info: filterContact || undefined,
        driver_type: filterType || undefined,
        has_assignment: filterAssignment !== null ? filterAssignment : undefined,
      };
      const response = await ouberService.getDrivers(params);
      
      const driversList = Array.isArray(response) ? response : (response?.data || []);

      const drivers = driversList.map(d => ({ 
        ...d, 
        key: d.driver_id.toString(), 
        id: d.driver_id.toString() 
      }));
      
      // Formats raw driver data into a consistent UI-friendly object structure
      setData(drivers);
    } catch (err) {
      showErrorToast("Fetch Error", "Failed to load drivers.");
    }
  }, [filterDriver, filterContact, filterType, filterAssignment, ouberService]);

  const fetchDriversRef = useRef(fetchDrivers);
  useEffect(() => {
    fetchDriversRef.current = fetchDrivers;
  }, [fetchDrivers]);

  // Implements a debounced fetch to handle live search filtering for the driver list
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchDriversRef.current();
    }, 500);
    return () => clearTimeout(handler);
  }, [filterDriver, filterContact, filterType, filterAssignment]);

  const filteredData = data;

  const handleEditDriver = (driver) => {
    setSelectedDriver(driver);
    setIsModalOpen(true);
  };

  const handleViewDriver = (driver) => {
    setSelectedDriver(driver);
    setIsViewModalOpen(true);
  };

  const handleCreateDriver = () => {
    setSelectedDriver(null);
    setIsModalOpen(true);
  };

  const handleDeleteDriver = (driver) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this driver?',
      content: `This will permanently remove ${driver.driver_name} from the registry.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await ouberService.deleteDriver(driver.id);
          showSuccessToast("Success", "Driver deleted successfully.");
          fetchDrivers();
        } catch (e) {
          showErrorToast("Error", "Failed to delete driver. They might be assigned to a trip.");
        }
      }
    });
  };

  const handleSaveDriver = async (values) => {
    try {
      if (selectedDriver) {
        await ouberService.updateDriver(selectedDriver.id, values);
        showSuccessToast("Success", "Driver updated successfully.");
      } else {
        await ouberService.createDriver(values);
        showSuccessToast("Success", "Driver created successfully.");
      }
      setIsModalOpen(false);
      fetchDrivers();
    } catch (e) {
      showErrorToast("Error", "Failed to save driver.");
    }
  };

  const columns = [
    { title: "Driver ID", dataIndex: "id", key: "id", align: "center" },
    {
      title: "Driver Name",
      dataIndex: "driver_name",
      key: "driver_name",
      align: "center",
    },
    {
      title: "Contact Information",
      dataIndex: "contact_number",
      key: "contact_number",
      align: "center",
    },
    { 
      title: "Driver Type", 
      dataIndex: "driver_type", 
      key: "driver_type", 
      align: "center",
    },
    {
      title: "Next Assignment",
      key: "next_assignment",
      align: "center",
      render: (_, record) => {
        const next = record.next_assignment;
        if (!next) return <span style={{ color: "#bfbfbf", fontSize: "12px" }}>None Scheduled</span>;

        return (
          <div style={{ fontSize: "12px", lineHeight: "1.2" }}>
            <div style={{ fontWeight: "bold", color: "#862633" }}>
              {next.trip_date ? next.trip_date.split('T')[0] : 'N/A'}
            </div>
            <div style={{ color: "#595959" }}>{next.dropoff_location || next.pickup_venue}</div>
          </div>
        );
      }
    },
    {
      title: "Created By",
      key: "created_by",
      align: "center",
      render: (_, record) => record.created_by
        ? `${record.created_by.first_name || ""} ${record.created_by.last_name || ""}`.trim()
        : <span style={{ color: "#bfbfbf", fontSize: "12px" }}>—</span>,
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <EyeOutlined
            style={{ fontSize: "18px", cursor: "pointer", color: "#1890ff" }}
            onClick={() => handleViewDriver(record)}
          />
          {canManage(record) && (
            <EditOutlined
              style={{ fontSize: "18px", cursor: "pointer", color: "#52c41a" }}
              onClick={() => handleEditDriver(record)}
            />
          )}
          {canManage(record) && (
            <DeleteOutlined
              style={{ fontSize: "18px", cursor: "pointer", color: "#f5222d" }}
              onClick={() => handleDeleteDriver(record)}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="ouber-page-container">
      <Space className="ouber-page-header">
        <Typography.Title level={3} className="ouber-page-title">
          Manage Drivers
          <OuberPageInfoIcon
            message="Driver Registry"
            description="View, edit, and manage system-wide drivers, their contact information, types, and next trip assignments."
          />
        </Typography.Title>
        <Space>
          <Button
            ref={createDriverButtonRef}
            type="primary"
            icon={<PlusCircleOutlined />}
            className="ouber-btn-forest"
            onClick={handleCreateDriver}
          >
            Create Driver
          </Button>
          <Tooltip title="Take a tour of this page">
            <Button
              icon={<QuestionCircleOutlined />}
              onClick={() => setIsTourOpen(true)}
            />
          </Tooltip>
        </Space>
      </Space>

      <div className="ouber-filter-container" ref={filtersRef}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Input 
              placeholder="Search Driver Name" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filterDriver}
              onChange={(e) => setFilterDriver(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input 
              placeholder="Search Contact Info" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filterContact}
              onChange={(e) => setFilterContact(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by Type"
              style={{ width: "100%" }}
              allowClear
              value={filterType}
              onChange={(val) => setFilterType(val)}
              options={[
                { value: "Employee", label: "Employee" },
                { value: "Outsourced", label: "Outsourced" },
                { value: "Carpool", label: "Carpool" },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Has Assignment?"
              style={{ width: "100%" }}
              allowClear
              value={filterAssignment}
              onChange={(val) => setFilterAssignment(val)}
              options={[
                { value: true, label: "Yes, Has Assignment" },
                { value: false, label: "No, Free" },
              ]}
            />
          </Col>
        </Row>
      </div>

      <div ref={viewControlsRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: "12px" }}>
        <DataView />
        <Button
          icon={<SyncOutlined />}
          onClick={fetchDrivers}
          loading={loading}
          className="ouber-btn-primary"
          style={{ marginBottom: 0 }}
        >
          Refresh
        </Button>
      </div>

      <div ref={tableRef}>
        {dataView === "table" ? (
          <PvTable
            data={filteredData}
            columns={columns}
            loading={loading}
            onRowClick={() => {}}
          />
        ) : (
          <PvCardList
            data={filteredData}
            columns={columns}
            onRowClick={() => {}}
            loading={loading}
            maxActiveRow={filteredData.length}
          />
        )}
      </div>

      <AddDriverModal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onSave={handleSaveDriver}
        initialValues={selectedDriver}
      />

      <AdminViewDriverModal
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        driver={selectedDriver}
      />

      <Tour open={isTourOpen} onClose={handleTourClose} steps={tourSteps} />
    </div>
  );
};

export default ManageDriverPage;
